/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import classNames from "classnames";

import SVGIcon from "components/SVGIcon";
import useWindowDimensions, { breakpoints } from "helpers/windowDimensions";
import * as Helpers from 'hooks/helpers';


const { useFirstViewableListItemIndex } = Helpers;

const getValue = (data, key) => data[key];

// StyleGuide = molecules/lists/table

// TableColumn is a non-rendering Component
// It only exists to define the structure for the <Table> component
const TableColumn = () => null;

/* eslint-disable react/no-unused-prop-types, react-redux/no-unused-prop-types */
TableColumn.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  cellClassName: PropTypes.string,
  expandRow: PropTypes.bool,
  sortable: PropTypes.oneOfType([
    PropTypes.bool, // uses same result as cellValue unless a string or function is provided
    PropTypes.string,
    PropTypes.func
  ]),
  sortDefault: PropTypes.oneOf([
    false,
    true, // same as "desc"
    "asc",
    "desc"
  ]),
  cellValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]).isRequired,
  cellDefault: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string
  ]),
  tabIndex: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  headerTabIndex: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  cellTabIndex: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ])
};
/* eslint-enable react/no-unused-prop-types, react-redux/no-unused-prop-types */

export { TableColumn };

const TableExpandableRow = () => null;

/* eslint-disable react/no-unused-prop-types, react-redux/no-unused-prop-types */
TableExpandableRow.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  rowValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]).isRequired,
  rowDefault: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string
  ]),
  rowClassName: PropTypes.string
};
/* eslint-enable react/no-unused-prop-types, react-redux/no-unused-prop-types */

export { TableExpandableRow };

/* eslint-disable-next-line react/no-multi-comp */
const Table = ({
  rowData,
  className = "",
  noHeader,
  zebra = false,
  scrollable,
  border,
  rowLinkTo,
  rowDisable,
  rowOnClick,
  refs,
  highlightId,
  children,
  refContainer,
  scrollToRef,
  fullScreen,
  onHover,
  onScroll,
  maxRecords,
  lastElementRef,
  disableAriaLabel = false,
  sortCallback = () => {},
  selectable = false,
  setSelectedRows,
  selectedRows
}) => {
  const { width: screenWidth } = useWindowDimensions();
  const isSmallScreen = screenWidth <= breakpoints.sm;
  // Make sure we have an array of TableColumns from children
  // TODO: Should filter out components which aren't instances of TableColumn. Enforcing this with PropTypes is not supported.
  const tableColumns = (!Array.isArray(children) ? [children] : children)
    .filter(child => child)
    .map(child => ({ ...child.props, key: child.key }));

  // Determine key for each TableColumn (if not provided)
  tableColumns.forEach((tableColumn, i) => {
    if (!tableColumn.key) {
      const { cellValue } = tableColumn;
      tableColumn.key = typeof cellValue === 'function' ? i : cellValue;
    }
  });

  // Determine default sortKey and sortDirection for Table
  const defaultSortColumn = tableColumns.find(tableColumn => tableColumn.sortDefault);
  const defaultSortKey = defaultSortColumn ? defaultSortColumn.key : null;
  const defaultSortDirection = defaultSortColumn && defaultSortColumn.sortDefault === "asc" ? -1 : 1;
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);
  const [upperLimit, setUpperLimit] = useState(maxRecords || Infinity);
  const adjustSpliceRange = useCallback((idx) => {
    if (upperLimit < idx + (maxRecords / 2)) {
      setUpperLimit(maxRecords + idx);
    }
  }, [maxRecords, upperLimit]);
  const { refContainer: maxRecordRef, debouncedScroll } = useFirstViewableListItemIndex(adjustSpliceRange, [upperLimit]);

  const onClickSort = (key, direction) => {
    if (key !== sortKey) {
      setSortKey(() => key);
      setSortDirection(1);
    } else if (direction !== sortDirection) { setSortDirection(direction); }
  };

  useEffect(() => {
    if (maxRecords) {
      setUpperLimit(maxRecords);
    }
  }, [maxRecords, rowData.length]);

  useEffect(() => {
    const paginationSortDirection = [-1, 0].includes(sortDirection) ? 0 : 1;
    if (sortKey) sortCallback(`${sortKey},${paginationSortDirection}`);
  }, [sortKey, sortDirection, sortCallback]);

  // Handlers for checkbox selection
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const selectableRows = rowData.filter(row => getValue(row, 'role') !== 'super admin').map(row => getValue(row, 'id'));
      setSelectedRows(true, selectableRows);
    } else {
      setSelectedRows(false, []);
    }
  };

  const handleSelectRow = (event, rowId) => {
    setSelectedRows(event.target.checked, rowId);
  };

  // Determine which TableColumn we are sorting by (if any)
  const sortChild = tableColumns.find(tableColumn => tableColumn.sortable && tableColumn.key === sortKey);

  // Apply sort order if needed
  const sortRows = (a, b, tableColumn, direction) => {
    // Determine sortable values
    const { sortable, cellValue } = tableColumn;
    const [sortA, sortB] = (() => {
      if (typeof sortable === 'function') return [sortable(a), sortable(b)];
      if (typeof sortable === 'string') return [a[sortable], b[sortable]];
      if (typeof cellValue === 'function') return [cellValue(a), cellValue(b)];
      return [a[cellValue], b[cellValue]];
    })();

    // Tie
    if (sortA === sortB) {
      // If we have a default sort column then we use it as the tiebreaker (unless we're already sorting with it)
      if (defaultSortColumn && defaultSortKey !== sortKey && tableColumn !== defaultSortColumn) {
        return sortRows(a, b, defaultSortColumn, defaultSortDirection);
      }
      return 0;
    }

    let result = sortA > sortB ? -1 : 1;
    // Apply sort direction
    result *= direction === 1 ? -1 : 1;
    // Reverse when sorting numeric values
    if (typeof sortA === 'number') result *= -1;
    return result;
  };
  const sortedRowData = sortChild ? rowData.sort((a, b) => sortRows(a, b, sortChild, sortDirection)) : rowData;
  // Ref prop
  const containerRef = refContainer || maxRecordRef;

  const renderHeader = (tableColumn, i) => {
    const {
      children: headerChildren,
      className: headerClassName,
      key,
      sortable,
      headerTabIndex,
      tabIndex: columnTabIndex
    } = tableColumn;

    let ariaSort = 'none';

    if (i === 0 && selectable) {
      return (
        <div
          role="columnheader"
          key={i}
          className="col col-select"
          {...{ "aria-sort": ariaSort }}
        >
          <input
            type="checkbox"
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && e.target.click()}
            checked={
              selectedRows.length === rowData.filter(row => getValue(row, 'role') !== 'super admin').length
                && selectedRows.length > 0
            }
            onChange={e => handleSelectAll(e)}
          />
        </div>
      );
    }

    const handleKeyPres = (ev) => {
      if ([32, 13].includes(ev.keyCode)) {
        onClickSort(key, +!sortDirection);
      }
    };

    // UpCaret default on Quircles Column doesn't display for QuestionList ... why?
    let caret = (<SVGIcon name="RightCaret1" width={12} height={12} minWidth={12} ariaLabel="Sort" />);
    if (sortKey === key) {
      if (sortDirection !== 1) {
        caret = (<SVGIcon name="UpCaret1" width={12} height={12} minWidth={12} ariaLabel="Sort" />);
        ariaSort = 'ascending';
      } else {
        caret = (<SVGIcon name="DownCaret1" width={12} height={12} minWidth={12} ariaLabel="Sort" />);
        ariaSort = 'descending';
      }
    }

    return (
      /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions  */
      <div tabIndex={headerTabIndex ?? columnTabIndex ?? (sortable ? 0 : -1)} role="columnheader" key={i} onClick={sortable ? () => onClickSort(key, +!sortDirection) : null} onKeyDown={sortable ? handleKeyPres : null} className={`col ${headerClassName}`} {...{ 'aria-sort': ariaSort }}>
        { headerChildren }
        { sortable && caret }
        {ariaSort !== 'none' && (<span className="screenreader" role="alert" aria-live="assertive">{ariaSort}</span>)}
      </div>
    );
  };

  const renderCell = (row, cellValue, cellDefault, i) => {
    if (typeof cellValue === 'function') return cellValue(row, i);
    if (typeof cellValue === 'string') {
      if (row[cellValue]) return row[cellValue];
    }
    return cellDefault;
  };

  const renderCells = (row, i) =>
    tableColumns.map((tableColumn, j) => {
      const cellTabIndex = tableColumn.cellTabIndex ?? tableColumn.tabIndex;

      if (tableColumn.expandRow) {
        return (
          <div tabIndex={cellTabIndex} role="cell" className="row w-100" key={j}>
            { renderCell(row, tableColumn.cellValue, tableColumn.cellDefault, i) }
          </div>
        );
      }
      return (
        <div tabIndex={cellTabIndex} role="cell" className={`col ${tableColumn.className} ${tableColumn.cellClassName}`} key={j}>
          { renderCell(row, tableColumn.cellValue, tableColumn.cellDefault, i) }
        </div>
      );
    });

  const renderRow = (row, i) => {
    if (row === undefined || row === null) {
      return null;
    }
    const rowId = row.id || 0;
    /* This whole ref thing is for scrolling, right now have it working fine in question list which is scrollToRef.
      The refs prop and the containerRef are how it works in the performance timeline which perhaps can be
      done the same way scrollToRef is done but not sure because the scroll is not on the page but in an element
    */
    let ref = refs ? refs[rowId] : React.createRef();
    ref = scrollToRef && scrollToRef[rowId] ? scrollToRef[rowId] : ref;
    ref = lastElementRef && rowData.length === i + 1 ? lastElementRef : ref;
    const highlight = rowId === highlightId && highlightId ? 'highlight' : "";
    const rowTableClass = classNames(`row table-row ${isSmallScreen ? 'h-auto' : ''}`, { highlight, 'border-bottom': border });
    const rowDisabled = rowDisable && rowDisable(row);
    const hoverEvents = onHover ? {
      onFocus: () => onHover(getValue(row, 'id')),
      onMouseOver: () => onHover(getValue(row, 'id'))
    } : {};

    const handleKeyPres = (ev) => {
      if ([32, 13].includes(ev.keyCode)) {
        if (ev.target.closest('button, a, input, [role="button"]')) {
          return;
        }
        ev.preventDefault();
        rowOnClick(row, ev);
      }
    };

    if (selectable) {
      return (
        <div
          role="row"
          className={`row table-row ${isSmallScreen ? "h-auto" : ""} ${highlight} ${
            border ? "border-bottom" : ""
          }`}
          key={rowId}
        >
          <div
            role="cell"
            className="col col-select checkbox"
          >
            {getValue(row, 'role') !== 'super admin' && (
              <input
                type="checkbox"
                checked={selectedRows.includes(getValue(row, 'id'))}
                onChange={e => handleSelectRow(e, getValue(row, 'id'))}
                tabIndex="0"
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && e.target.click()}
              />
            )}
          </div>
          {renderCells(row, i)}
        </div>
      );
    }

    if (rowLinkTo && !rowDisabled) {
      if (rowOnClick) {
        return (
          <Link role="row" key={i} ref={ref} className={`${rowTableClass} btn d-flex btn-none`} to={rowLinkTo(row)} onClick={ev => rowOnClick(ev)}>{ renderCells(row, i) }</Link>
        );
      }

      return (
        <Link role="row" id={getValue(row, 'id')} {...hoverEvents} key={i} ref={ref} className={`${rowTableClass} btn d-flex btn-none`} to={rowLinkTo(row)}>{ renderCells(row, i) }</Link>
      );
    }
    if (rowOnClick && !rowDisabled) {
      return (
        <div key={i} ref={ref} {...hoverEvents} {...(!disableAriaLabel && { 'aria-label': `Go To ${getValue(row, 'name')}` })} className={`${rowTableClass} pad-top-btm clickable-row`} role="row" tabIndex={0} onClick={ev => rowOnClick(row, ev)} onKeyDown={handleKeyPres}>{ renderCells(row, i) }</div>
      );
    }
    return <div role="row" key={i} ref={ref} className={`${rowTableClass} pad-top-btm`}>{ renderCells(row, i) }</div>;
  };

  const tableClass = classNames('Table', {
    container: !fullScreen,
    'container-fluid': fullScreen,
    [className]: className
  });

  const handleScroll = (e) => {
    if (onScroll) onScroll(e);
    if (maxRecords) debouncedScroll(e);
  };

  const useOneScroll = !!onScroll || !!maxRecords;

  return (
    <div role="table" className={tableClass}>
      <div role="row" className={`row table-header-row ${noHeader ? 'd-none' : ''} ${scrollable ? 'scrollable' : ''} ${border ? 'border-bottom' : ''} ${selectable ? 'selectable' : ''}`}>
        {selectable ? ([
          tableColumns.filter(tc => !tc.expandRow)[0], ...tableColumns.filter(tc => !tc.expandRow)].map(renderHeader)
        ) : (
          tableColumns.filter(tc => !tc.expandRow).map(renderHeader)
        )}
      </div>
      <div role="rowgroup" ref={containerRef} {...(useOneScroll ? { onScroll: e => handleScroll(e) } : {})} className={`position-relative columns-container ${scrollable ? 'scrollable' : ''} ${zebra ? 'zebra' : ''}`}>
        { sortedRowData.slice(0, upperLimit).map(renderRow) }
      </div>
    </div>
  );
};

Table.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.element, PropTypes.bool]))
  ]).isRequired,
  rowData: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.number, PropTypes.string])).isRequired,
  rowLinkTo: PropTypes.func,
  rowOnClick: PropTypes.func,
  rowDisable: PropTypes.func,
  className: PropTypes.string,
  zebra: PropTypes.bool,
  scrollable: PropTypes.bool,
  refs: PropTypes.object,
  refContainer: PropTypes.object,
  scrollToRef: PropTypes.object,
  highlightId: PropTypes.number,
  maxRecords: PropTypes.number,
  border: PropTypes.bool,
  fullScreen: PropTypes.bool,
  onHover: PropTypes.func,
  onScroll: PropTypes.func,
  noHeader: PropTypes.bool,
  lastElementRef: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  disableAriaLabel: PropTypes.bool,
  sortCallback: PropTypes.func,
  selectable: PropTypes.bool,
  setSelectedRows: PropTypes.func,
  selectedRows: PropTypes.arrayOf(PropTypes.number)
};

export default Table;
